import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { carePlanService } from '../services/carePlanService'
import { Modal } from './Modal'
import './CarePlanTemplateSelector.css'

interface CarePlanTemplateSelectorProps {
  patientId: string
  onClose: () => void
  onSuccess: () => void
}

export function CarePlanTemplateSelector({
  patientId,
  onClose,
  onSuccess,
}: CarePlanTemplateSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()

  const { data: templates, isLoading } = useQuery({
    queryKey: ['care-plan-templates', selectedCategory],
    queryFn: () => carePlanService.getTemplates(selectedCategory === 'all' ? undefined : selectedCategory),
  })

  const createFromTemplateMutation = useMutation({
    mutationFn: (templateId: string) => carePlanService.createFromTemplate(patientId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plans', patientId] })
      onSuccess()
      onClose()
    },
  })

  const filteredTemplates = templates?.filter((template) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      template.title.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.templateCategory?.toLowerCase().includes(query)
    )
  })

  const formatCategoryName = (category: string): string => {
    return category
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const handleTemplateSelect = (templateId: string) => {
    createFromTemplateMutation.mutate(templateId)
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Care Plan from Template" size="large">
      <div className="care-plan-template-selector">
        <div className="template-selector-filters">
          <div className="filter-group">
            <label htmlFor="category-filter">Category</label>
            <select
              id="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="form-control"
            >
              <option value="all">All Categories</option>
              <option value="diabetes">Diabetes</option>
              <option value="cardiac">Cardiac</option>
              <option value="respiratory">Respiratory</option>
              <option value="mental-health">Mental Health</option>
              <option value="chronic-disease">Chronic Disease</option>
              <option value="general">General</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="search-filter">Search</label>
            <input
              id="search-filter"
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="template-selector-loading">
            <p>Loading templates...</p>
          </div>
        ) : !filteredTemplates || filteredTemplates.length === 0 ? (
          <div className="template-selector-empty">
            <p>No templates found.</p>
            {searchQuery && <p className="hint">Try adjusting your search or category filter.</p>}
          </div>
        ) : (
          <div className="template-selector-list">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="template-selector-item"
                onClick={() => handleTemplateSelect(template.id)}
              >
                <div className="template-item-header">
                  <h3>{template.title}</h3>
                  {template.templateCategory && (
                    <span className={`template-category category-${template.templateCategory}`}>
                      {formatCategoryName(template.templateCategory)}
                    </span>
                  )}
                </div>
                {template.description && (
                  <p className="template-item-description">{template.description}</p>
                )}
                {(template.goals?.length || template.interventions?.length) && (
                  <div className="template-item-stats">
                    {template.goals?.length > 0 && (
                      <span>{template.goals.length} goal{template.goals.length !== 1 ? 's' : ''}</span>
                    )}
                    {template.interventions?.length > 0 && (
                      <span>
                        {template.interventions.length} intervention{template.interventions.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {createFromTemplateMutation.isError && (
          <div className="error-message" role="alert">
            Failed to create care plan from template: {String(createFromTemplateMutation.error)}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={createFromTemplateMutation.isPending}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
